import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {domToReact, Element, HTMLReactParserOptions} from 'html-react-parser';
import {CtaContent} from './types';
import {CtaCard} from './renderers';

// ─── html-react-parser option builders ───────────────────────────────────────
// Detect WordPress-rendered blocks in the parsed DOM, extract their content, and
// swap in our designs. Each builder keeps its own one-off markup inline; only the
// reusable CtaCard is imported from `./renderers`.

/**
 * Replacer that upgrades content images (`img.bdn-content-image`) from raw <img> tags
 * to next/image. Falls back to the original node if the src is missing or dimensions
 * can't be determined.
 */
export const replaceContentImage = (node: any): React.ReactElement | undefined => {
    if (!(node instanceof Element) || node.name !== 'img') return undefined;
    const cls = node.attribs?.class || '';
    if (!cls.includes('bdn-content-image')) return undefined;

    const src = node.attribs?.src;
    if (!src) return undefined;

    // WordPress usually emits width/height attrs; fall back to a 3:2 box so next/image
    // (which requires intrinsic dimensions) still has an aspect ratio to work with.
    const width = Number(node.attribs?.width) || 1200;
    const height = Number(node.attribs?.height) || Math.round(width * 0.625);

    // Content images can be hosted on arbitrary external domains (e.g. images pasted from
    // Google Docs land on *.googleusercontent.com). The next/image optimizer 400s for any
    // host not in next.config `remotePatterns`, silently breaking those images. Rather than
    // allowlisting every possible host, serve non-first-party images unoptimized so the
    // original URL is used directly — first-party WP/CDN images still get full optimization.
    const isFirstParty = /^https?:\/\/([^/]*\.)?(debutinfotech\.(com|in)|d302yylkegfpwc\.cloudfront\.net)\//i.test(src);

    return (
        <Image
            src={src}
            alt={node.attribs?.alt || ''}
            width={width}
            height={height}
            className={cls}
            loading="lazy"
            unoptimized={!isFirstParty}
            // Article body is full-width on mobile and caps around ~800px on desktop.
            sizes="(max-width: 768px) 100vw, 800px"
            style={{width: '100%', height: 'auto'}}
        />
    );
};

// ─── Shared DOM traversal helpers ────────────────────────────────────────────
// Pure AST utilities consumed only by the option builders below. Kept local to
// this module (their sole consumer) rather than a separate util file.

function hasClass(node: unknown, cls: string): node is Element {
    return node instanceof Element && typeof node.attribs?.class === 'string' && node.attribs.class.includes(cls);
}

// Recursively collect visible text from a DOM node (flattens nested <strong> etc.).
function collectText(node: any): string {
    if (!node) return '';
    if (node.type === 'text') return node.data ?? '';
    if (Array.isArray(node.children)) return node.children.map(collectText).join('');
    return '';
}

// Depth-first: first descendant Element matching a predicate.
function findNode(node: any, pred: (n: Element) => boolean): Element | null {
    if (!node?.children) return null;
    for (const child of node.children) {
        if (child instanceof Element) {
            if (pred(child)) return child;
            const nested = findNode(child, pred);
            if (nested) return nested;
        }
    }
    return null;
}

/**
 * Extracts text content from a dom node tree recursively.
 */
function extractText(node: any): string {
    if (!node) return '';
    if (node.type === 'text') return node.data ?? '';
    if (node.children) return node.children.map(extractText).join('');
    return '';
}

// ─── CTA blocks ──────────────────────────────────────────────────────────────

function isDebutCtaBlock(domNode: unknown): domNode is Element {
    return (
        domNode instanceof Element &&
        domNode.name === 'div' &&
        typeof domNode.attribs?.class === 'string' &&
        domNode.attribs.class.includes('debut-cta-block')
    );
}

// Native WordPress CTA block (`wp-block-group cta-stack`): some posts ship the CTA
// as a native Gutenberg group block instead of our [cta_block] shortcode /
// debut-cta-block div, and with EMPTY cta_blocks data. We detect that block and
// extract heading/text/button straight from the DOM.
function isWpCtaStack(domNode: unknown): domNode is Element {
    return domNode instanceof Element && domNode.name === 'div' && hasClass(domNode, 'cta-stack');
}

// Pull a CtaContent out of a native cta-stack block's DOM subtree.
function extractWpCtaStack(domNode: Element): CtaContent {
    const headingNode = findNode(domNode, (n) => hasClass(n, 'cta-header'));
    const textNode = findNode(domNode, (n) => hasClass(n, 'cta-text'));
    const anchor = findNode(domNode, (n) => n.name === 'a');
    return {
        // Native cta-stack blocks carry no badge in the markup, so we default one
        // to match the badge shown on cta_blocks-driven CTAs.
        tag: 'Recommended Read',
        title: collectText(headingNode).trim(),
        description: collectText(textNode).trim(),
        btnLabel: collectText(anchor).trim(),
        btnHref: anchor?.attribs?.href ?? ''
    };
}

/**
 * Builds parse options that replace each WordPress-rendered
 * <div class="debut-cta-block"> with our bdn-cta-card design, pulling the
 * content for the Nth block from `ctaList[N]`. Mirrors the numbered
 * [related_post_N] handling: [cta_block_1] → ctaList[0], [cta_block_2] → ctaList[1], …
 * Falls back to the last entry if more divs appear than array items.
 */
export function makeCtaBlocksParseOptions(ctaList: CtaContent[]): HTMLReactParserOptions {
    let index = 0;
    return {
        replace: (domNode) => {
            // Our shortcode/div form — content comes from the cta_blocks payload.
            if (isDebutCtaBlock(domNode)) {
                const cta = ctaList[index] ?? ctaList.at(-1);
                index += 1;
                if (!cta || !cta.title) return undefined;
                return <CtaCard cta={cta} key={`cta-${index}`} />;
            }
            // Native WordPress group block — content lives in the DOM, not cta_blocks.
            if (isWpCtaStack(domNode)) {
                const cta = extractWpCtaStack(domNode);
                index += 1;
                if (!cta.title) return undefined;
                return <CtaCard cta={cta} key={`cta-stack-${index}`} />;
            }
            return undefined;
        }
    };
}

// Backward-compatible single-CTA variant (legacy `cta` object).
export function makeCtaBlockParseOptions(cta: CtaContent): HTMLReactParserOptions {
    return makeCtaBlocksParseOptions([cta]);
}

// ─── Related / read-more cards ───────────────────────────────────────────────

/**
 * Builds html-react-parser options that replace WordPress-rendered
 * <a class="debut-related-card"> elements with the bdn-related-section design.
 * Pass the current post's category name so it shows the right label.
 */
export function makeRelatedCardParseOptions(categoryLabel: string): HTMLReactParserOptions {
    return {
        replace: (domNode) => {
            if (
                domNode instanceof Element &&
                domNode.name === 'a' &&
                typeof domNode.attribs?.class === 'string' &&
                domNode.attribs.class.includes('debut-related-card')
            ) {
                const href = domNode.attribs.href ?? '#';
                // Extract img node
                const imgNode = domNode.children?.flatMap((c: any) => c.children ?? [c]).find((c: any) => c.name === 'img') as
                    | Element
                    | undefined;
                const imgSrc = imgNode?.attribs?.src ?? '';
                const imgAlt = imgNode?.attribs?.alt ?? '';
                // Extract h4 title
                const h4Node = domNode.children?.flatMap((c: any) => c.children ?? [c]).find((c: any) => c.name === 'h4') as
                    | Element
                    | undefined;
                const title = h4Node ? extractText(h4Node) : '';

                // Convert WordPress URL to relative path for next/link
                let linkHref = href;
                try {
                    const u = new URL(href);
                    linkHref = u.pathname;
                } catch {}

                return (
                    <div className="bdn-related-section">
                        <div className="bdn-related-section__list">
                            <Link href={linkHref} className="bdn-related-section__item" target="_blank" rel="noopener noreferrer">
                                <div className="bdn-related-section__thumb">
                                    {imgSrc && <Image src={imgSrc} alt={imgAlt} width={170} height={94} loading="lazy" />}
                                </div>
                                <div className="bdn-related-section__content">
                                    <span className="bdn-related-section__category">{categoryLabel || 'Blog'}</span>
                                    <span className="bdn-related-section__title">{title}</span>
                                </div>
                                <span className="bdn-related-section__arrow">&#8594;</span>
                            </Link>
                        </div>
                    </div>
                );
            }
            return undefined;
        }
    };
}

/**
 * Builds html-react-parser options that replace WordPress "Read more – <a>..." paragraphs
 * with the bdn-related-section card design.
 * Matches any <p> whose text starts with "read more"/"read also" and contains a link.
 * `posts` — category/related posts array used to look up thumbnail images by slug.
 */
export function makeReadMoreParseOptions(categoryLabel: string, posts: any[] = []): HTMLReactParserOptions {
    const findLink = (node: any): Element | undefined => {
        if (node instanceof Element && node.name === 'a') return node;
        if (node.children) {
            for (const child of node.children) {
                const found = findLink(child);
                if (found) return found;
            }
        }
        return undefined;
    };

    return {
        replace: (domNode) => {
            if (!(domNode instanceof Element) || domNode.name !== 'p') return undefined;
            const text = extractText(domNode).trim().toLowerCase();
            // Match "Read more …" plus older-blog variants ("Read also …",
            // "Read also this …"). \b avoids false positives like "Ready to …".
            if (!/^read (more|also)\b/.test(text)) return undefined;
            const linkNode = findLink(domNode);
            if (!linkNode) return undefined;
            const href = linkNode.attribs?.href ?? '#';
            const linkText = extractText(linkNode);
            if (!linkText) return undefined;

            let linkHref = href;
            let slug = '';
            try {
                const u = new URL(href);
                linkHref = u.pathname;
                slug = u.pathname.replace(/\/$/, '').split('/').pop() ?? '';
            } catch {
                slug = href.replace(/\/$/, '').split('/').pop() ?? '';
            }

            // Try to find matching post image by slug
            const matchedPost = posts.find((p: any) => p.slug === slug);
            const thumbSrc = matchedPost?.featured_image_url || matchedPost?.blog_image || '';
            const thumbTitle = matchedPost
                ? typeof matchedPost.title === 'string'
                    ? matchedPost.title
                    : matchedPost.title?.rendered ?? linkText
                : linkText;

            return (
                <div className="bdn-related-section">
                    <div className="bdn-related-section__list">
                        <Link
                            href={linkHref}
                            className={`bdn-related-section__item${thumbSrc ? '' : ' bdn-related-section__item--no-thumb'}`}
                        >
                            {thumbSrc && (
                                <div className="bdn-related-section__thumb">
                                    <Image src={thumbSrc} alt={thumbTitle} width={170} height={94} loading="lazy" />
                                </div>
                            )}
                            <div className="bdn-related-section__content">
                                <span className="bdn-related-section__category">{categoryLabel || 'Blog'}</span>
                                <span className="bdn-related-section__title">{thumbTitle}</span>
                            </div>
                            <span className="bdn-related-section__arrow">&#8594;</span>
                        </Link>
                    </div>
                </div>
            );
        }
    };
}

// ─── News table ──────────────────────────────────────────────────────────────
// Self-contained option that promotes the first row of a table to header cells.
// Kept whole (option + renderer fused) because it recurses through domToReact
// with its own options — extracting the markup would add indirection for no gain.
export const newsTableOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
        if (domNode instanceof Element && domNode.name === 'tr') {
            const parent = domNode.parent as Element;
            if (parent && (parent.name === 'tbody' || parent.name === 'table')) {
                const trSiblings = parent.children.filter((c: any) => c.name === 'tr');
                if (trSiblings[0] === domNode) {
                    return (
                        <tr {...domNode.attribs}>
                            {domNode.children?.map((child: any, index: number) => {
                                if (child.name === 'td') {
                                    const children =
                                        child.children?.length === 1 && child.children[0].name === 'strong'
                                            ? child.children[0].children
                                            : child.children;
                                    return (
                                        <th key={index} {...child.attribs}>
                                            {domToReact(children, newsTableOptions)}
                                        </th>
                                    );
                                }
                                return domToReact([child], newsTableOptions);
                            })}
                        </tr>
                    );
                }
            }
        }
        return undefined;
    }
};
